import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";
import PdfRender from "../../../components/pdfRender/pdfRender";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";

interface SimpleDocViewerModalProps {
  onClose: () => void;
}

const SimpleDocViewerModal: React.FC<SimpleDocViewerModalProps> = ({
  onClose,
}) => {
  const { metaData } = useSelector((state: RootState) => state.modal);

  const title = metaData?.title || "Document Viewer";

  // Support both single docUrl (backward compatibility) and docUrls array
  const docUrls =
    metaData?.docUrls || (metaData?.docUrl ? [metaData.docUrl] : []);
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentUrl = docUrls[currentIndex] || "";
  const hasMultiple = docUrls.length > 1;

  // Check if the URL is an image (jpg, jpeg, png, gif, etc.)
  const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)(\?|$)/i.test(currentUrl);

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(docUrls.length - 1, prev + 1));
  };

  return (
    <Modal
      dialogId={ModalId.DOC_VIEWER_MODAL_WITH_TITLE}
      onSave={() => {}}
      title={title}
      saveButtonText="Close"
      cancelText="Cancel"
      heightPercentage={90}
      onCancel={onClose}
      onClose={onClose}
      hideFooter={false}
    >
      <div className="flex h-full w-full flex-col">
        <div className="simple-doc-viewer-modal flex-1 overflow-scroll">
          {currentUrl ? (
            isImage ? (
              <div className="flex h-full w-full items-center justify-center p-4">
                <img
                  src={currentUrl}
                  alt={title}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ) : (
              <PdfRender file={currentUrl} />
            )
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <p className="text-gray-600">No document available</p>
            </div>
          )}
        </div>

        {/* Navigation controls - only show if multiple documents */}
        {hasMultiple && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                currentIndex === 0
                  ? "cursor-not-allowed bg-gray-200 text-gray-400"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              Previous
            </button>

            <span className="text-sm font-medium text-gray-700">
              {currentIndex + 1} of {docUrls.length}
            </span>

            <button
              onClick={handleNext}
              disabled={currentIndex === docUrls.length - 1}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                currentIndex === docUrls.length - 1
                  ? "cursor-not-allowed bg-gray-200 text-gray-400"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SimpleDocViewerModal;
